import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import ReactMarkdown from 'react-markdown'
import remarkGemoji from 'remark-gemoji'
import remarkGfm from 'remark-gfm'
import remarkSmartypants from 'remark-smartypants'
import { useLocation, useNavigate } from 'react-router-dom'
import { Virtuoso, VirtuosoHandle } from 'react-virtuoso'
import { useThemeMode } from '@/contexts/ThemeContext'
import useIsMobile from '@/hooks/useIsMobile'
import { normalizeEmojis } from '@/lib/emojiUtils'
import { theme } from '@/lib/theme'
import type { ChatMessageWithTimestamp } from '@/types/chat/ChatMessage'
import Message from '../ui/Message'
import { ToolInvocations } from './ToolInvocations'

// CodeBlock component with copy button (for fenced code blocks inside <pre>)
// Inline <code> elements are handled separately and don't get copy buttons
function CodeBlock({
  children,
  fontFamily,
  colors,
}: {
  children?: React.ReactNode
  fontFamily: string
  colors: Record<string, string>
}) {
  const [copied, setCopied] = useState(false)

  // Extract text content from children for copying
  const getTextContent = (node: React.ReactNode): string => {
    if (typeof node === 'string') return node
    if (typeof node === 'number') return String(node)
    if (!node) return ''
    if (Array.isArray(node)) return node.map(getTextContent).join('')
    if (React.isValidElement(node)) {
      const props = node.props as { children?: React.ReactNode }
      if (props.children) {
        return getTextContent(props.children)
      }
    }
    return ''
  }

  const textContent = getTextContent(children)

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(textContent)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      console.error('Failed to copy:', err)
    }
  }

  return (
    <pre
      style={{
        fontFamily,
        whiteSpace: 'pre-wrap',
        overflowWrap: 'break-word',
        wordBreak: 'break-word',
        backgroundColor: colors.secondaryBg,
        padding: '0.75em 1em',
        borderRadius: '4px',
        margin: '0.5em 0',
        border: `1px solid ${colors.primaryBorder}`,
        overflow: 'auto',
        position: 'relative',
      }}
    >
      <button
        onClick={handleCopy}
        title={copied ? 'Copied!' : 'Copy code'}
        style={{
          position: 'absolute',
          top: '6px',
          right: '6px',
          padding: '4px 6px',
          fontSize: '14px',
          backgroundColor: colors.primaryBorder,
          border: 'none',
          borderRadius: '3px',
          cursor: 'pointer',
          color: colors.secondaryText,
          opacity: 0.7,
          transition: 'opacity 0.2s',
          lineHeight: 1,
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.opacity = '1'
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.opacity = '0.7'
        }}
      >
        {copied ? '✓' : '⧉'}
      </button>
      {children}
    </pre>
  )
}

interface MessagesDisplayProps {
  messages: ChatMessageWithTimestamp[]
  isLoadingMessages: boolean
  fontMode?: 'departure-mono' | 'open-dyslexic' | 'system'
  targetMessageId?: string
  onTargetMessageScrolled?: () => void
  hasMoreMessages?: boolean
  isLoadingMoreMessages?: boolean
  onLoadMore?: () => void
  hasMorePreviousMessages?: boolean
  isLoadingPreviousMessages?: boolean
  onLoadPrevious?: () => void
  onReplyToMessage?: (message: ChatMessageWithTimestamp) => void
  replyingToMessageId?: string | null
  currentSessionId?: string
  textSize?: number
}

// Helper to strip document tags for display
const stripDocumentTags = (content: string) => {
  return content
    .replace(/<USER_PASTED_DOCUMENT>([\s\S]*?)<\/USER_PASTED_DOCUMENT>/g, '$1')
    .trim()
}

// Factory function to create markdown components with consistent font and theme colors
const createMarkdownComponents = (
  fontFamily: string,
  isMonospace: boolean,
  colors: Record<string, string>
) => ({
  // Code blocks (fenced with ```)
  pre: ({ children }: { children?: React.ReactNode }) => (
    <CodeBlock fontFamily={fontFamily} colors={colors}>
      {children}
    </CodeBlock>
  ),
  // Inline code and code inside pre blocks
  code: ({ children, className }: { children?: React.ReactNode; className?: string }) => {
    const isCodeBlock = className?.startsWith('language-')
    return (
      <code
        style={{
          fontFamily,
          whiteSpace: 'pre-wrap',
          overflowWrap: 'break-word',
          wordBreak: 'break-word',
          ...(!isCodeBlock && {
            backgroundColor: colors.secondaryBg,
            padding: '0.15em 0.4em',
            borderRadius: '3px',
            border: `1px solid ${colors.primaryBorder}`,
          }),
        }}
      >
        {children}
      </code>
    )
  },
  strong: ({ children }: { children?: React.ReactNode }) => (
    <strong style={{ fontWeight: 'bold' }}>{children}</strong>
  ),
  em: ({ children }: { children?: React.ReactNode }) => (
    <em style={{ fontStyle: 'italic' }}>{children}</em>
  ),
  h1: ({ children }: { children?: React.ReactNode }) => (
    <h1
      style={{
        fontSize: isMonospace ? '1.4em' : '2em',
        marginTop: '0.67em',
        marginBottom: '0.67em',
        ...(isMonospace && {
          borderBottom: '2px solid currentColor',
          paddingBottom: '0.2em',
        }),
      }}
    >
      {children}
    </h1>
  ),
  h2: ({ children }: { children?: React.ReactNode }) => (
    <h2
      style={{
        fontSize: isMonospace ? '1.25em' : '1.5em',
        marginTop: '0.83em',
        marginBottom: '0.83em',
        ...(isMonospace && {
          borderBottom: '1px solid currentColor',
          paddingBottom: '0.15em',
        }),
      }}
    >
      {children}
    </h2>
  ),
  h3: ({ children }: { children?: React.ReactNode }) => (
    <h3
      style={{
        fontSize: isMonospace ? '1.1em' : '1.17em',
        marginTop: '1em',
        marginBottom: '1em',
        ...(isMonospace && {
          textTransform: 'uppercase' as const,
          letterSpacing: '0.05em',
        }),
      }}
    >
      {children}
    </h3>
  ),
  blockquote: ({ children }: { children?: React.ReactNode }) => (
    <blockquote
      style={{
        borderLeft: `3px solid ${colors.primaryAccent}`,
        paddingLeft: '1em',
        marginLeft: '0',
        marginRight: '0',
        opacity: 0.85,
      }}
    >
      {children}
    </blockquote>
  ),
  p: ({ children }: { children?: React.ReactNode }) => (
    <p style={{ whiteSpace: 'pre-wrap', margin: '0.5em 0' }}>{children}</p>
  ),
  ul: ({ children }: { children?: React.ReactNode }) => (
    <ul style={{ paddingLeft: '1.5em', margin: '0.5em 0' }}>{children}</ul>
  ),
  ol: ({ children }: { children?: React.ReactNode }) => (
    <ol style={{ paddingLeft: '1.5em', margin: '0.5em 0' }}>{children}</ol>
  ),
  li: ({ children }: { children?: React.ReactNode }) => (
    <li style={{ marginBottom: '0.25em' }}>{children}</li>
  ),
  hr: () => (
    <hr
      style={{
        border: 'none',
        borderTop: `1px solid ${colors.primaryBorder}`,
        margin: '1em 0',
      }}
    />
  ),
})

// Stable reference for remark plugins to avoid unnecessary re-renders
const remarkPlugins = [remarkGfm, remarkGemoji, remarkSmartypants]

// Large offset for prepending support - allows loading ~10000 pages of history
const START_INDEX = 100000


function MessagesDisplay({
  messages,
  isLoadingMessages,
  fontMode = 'system',
  targetMessageId,
  onTargetMessageScrolled,
  hasMoreMessages = false,
  isLoadingMoreMessages = false,
  onLoadMore,
  hasMorePreviousMessages = false,
  isLoadingPreviousMessages = false,
  onLoadPrevious,
  onReplyToMessage,
  replyingToMessageId,
  currentSessionId,
  textSize = 14,
}: MessagesDisplayProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { colors } = useThemeMode()
  const virtuosoRef = useRef<VirtuosoHandle>(null)

  const isMobile = useIsMobile()
  const [highlightedMessageId, setHighlightedMessageId] = useState<string | null>(null)
  const [copiedMessageId, setCopiedMessageId] = useState<string | null>(null)
  const [longPressedMessageId, setLongPressedMessageId] = useState<string | null>(null)
  const [isAtBottom, setIsAtBottom] = useState(true)
  const longPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Calculate firstItemIndex for prepending support
  const firstItemIndex = START_INDEX - messages.length

  /**
   * Scroll-to-bottom
   *
   * Virtuoso handles:
   * - Virtualization (only renders visible items)
   * - Prepending via firstItemIndex (loading older messages)
   * - Initial position via initialTopMostItemIndex
   *
   * Custom logic:
   * - Scroll-to-bottom on content changes (streaming + new messages)
   * - requestAnimationFrame defers scroll until Virtuoso measures the item
   * - Index: firstItemIndex + messages.length - 1 = last item's Virtuoso index
   */
  const lastMessageContent = messages.length > 0 ? messages[messages.length - 1]?.content : ''

  useEffect(() => {
    if (messages.length > 0) {
      requestAnimationFrame(() => {
        virtuosoRef.current?.scrollToIndex({
          index: firstItemIndex + messages.length - 1,
          align: 'end',
          behavior: 'auto',
        })
      })
    }
  }, [lastMessageContent, messages.length, firstItemIndex])

  const handleTouchStart = useCallback(
    (messageId: string) => {
      if (!isMobile) return
      longPressTimerRef.current = setTimeout(() => {
        setLongPressedMessageId(messageId)
      }, 400)
    },
    [isMobile]
  )

  const handleTouchEnd = useCallback(() => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current)
      longPressTimerRef.current = null
    }
  }, [])

  const handleTouchMove = useCallback(() => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current)
      longPressTimerRef.current = null
    }
  }, [])

  const handleCopyMessage = useCallback(
    async (content: string, messageId: string | undefined) => {
      if (!content || !navigator?.clipboard) return
      try {
        await navigator.clipboard.writeText(content)
        if (messageId) {
          setCopiedMessageId(messageId)
          setTimeout(() => {
            setCopiedMessageId((current) => (current === messageId ? null : current))
          }, 1500)
        }
      } catch {
        // Silently ignore copy failures
      }
    },
    []
  )

  // Cleanup long-press timer on unmount
  useEffect(() => {
    return () => {
      if (longPressTimerRef.current) {
        clearTimeout(longPressTimerRef.current)
      }
    }
  }, [])

  // Dismiss timestamp overlay when tapping elsewhere
  useEffect(() => {
    if (!longPressedMessageId) return
    const handleDismiss = () => setLongPressedMessageId(null)
    document.addEventListener('touchstart', handleDismiss)
    return () => document.removeEventListener('touchstart', handleDismiss)
  }, [longPressedMessageId])

  // Scroll to target message when specified
  useEffect(() => {
    if (targetMessageId && !isLoadingMessages && messages.length > 0) {
      const targetIndex = messages.findIndex((m) => m.id === targetMessageId)
      if (targetIndex !== -1) {
        virtuosoRef.current?.scrollToIndex({
          index: targetIndex,
          align: 'center',
          behavior: 'smooth',
        })
        setHighlightedMessageId(targetMessageId)
        const timer = setTimeout(() => {
          setHighlightedMessageId(null)
          onTargetMessageScrolled?.()
        }, 2000)
        return () => clearTimeout(timer)
      }
    }
  }, [targetMessageId, isLoadingMessages, messages, onTargetMessageScrolled])

  const fontFamily =
    fontMode === 'departure-mono'
      ? 'var(--font-departure-mono)'
      : fontMode === 'open-dyslexic'
        ? 'var(--font-open-dyslexic)'
        : 'inherit'

  const isMonospace = fontMode === 'departure-mono'

  const markdownComponents = useMemo(
    () => createMarkdownComponents(fontFamily, isMonospace, colors),
    [fontFamily, isMonospace, colors]
  )

  const messageStyles = useMemo(
    () => ({
      command: {
        backgroundColor: colors.assistantMsgBg,
        borderLeft: `3px solid ${colors.assistantMsgAccent}`,
        borderRadius: '10px',
      },
      assistant: {
        backgroundColor: colors.assistantMsgBg,
        borderLeft: `3px solid ${colors.assistantMsgAccent}`,
        borderRadius: '10px',
      },
      user: {
        backgroundColor: colors.userMsgBgAlpha,
        borderRight: `3px solid ${colors.userMsgAccent}`,
        borderRadius: '10px',
      },
      tool: {
        backgroundColor: colors.assistantMsgBg,
        borderLeft: `3px solid ${colors.assistantMsgAccent}`,
        borderRadius: '10px',
      },
    }),
    [colors]
  )

  // Handle reaching the start (top) of the list - load older messages
  const handleStartReached = useCallback(() => {
    if (hasMorePreviousMessages && !isLoadingPreviousMessages && onLoadPrevious) {
      onLoadPrevious()
    }
  }, [hasMorePreviousMessages, isLoadingPreviousMessages, onLoadPrevious])

  // Scroll to bottom when button clicked
  const handleScrollToBottom = useCallback(() => {
    virtuosoRef.current?.scrollToIndex({
      index: firstItemIndex + messages.length - 1,
      align: 'end',
      behavior: 'smooth',
    })
  }, [firstItemIndex, messages.length])

  // Render individual message item
  const renderMessage = useCallback(
    (index: number, message: ChatMessageWithTimestamp) => {
      const isHighlighted = message.id && message.id === highlightedMessageId
      const isBeingRepliedTo = message.id && message.id === replyingToMessageId
      const displayContent = stripDocumentTags(message.content)
      const replyPreview = message.replyTo ? stripDocumentTags(message.replyTo.content) : null

      return (
        <div
          data-message-role={message.role}
          style={{ marginBottom: '8px', position: 'relative', paddingLeft: '12px', paddingRight: '12px' }}
        >
          {/* Reply preview */}
          {message.replyTo && (
            <div
              style={{
                backgroundColor: colors.secondaryBg,
                borderLeft: `2px solid ${colors.primaryAccent}`,
                padding: '6px 8px',
                marginBottom: '8px',
                borderRadius: '4px',
                fontSize: '11px',
                opacity: 0.8,
              }}
            >
              <div
                style={{
                  fontWeight: 'bold',
                  marginBottom: '2px',
                  color: colors.secondaryText,
                }}
              >
                Replying to {message.replyTo.role}:
              </div>
              <div style={{ fontStyle: 'italic', color: colors.primaryText }}>
                {replyPreview?.substring(0, 100)}
                {(replyPreview?.length ?? 0) > 100 ? '...' : ''}
              </div>
              <button
                onClick={() => {
                  if (currentSessionId && message.replyTo?.messageId) {
                    navigate(`?session=${currentSessionId}&message=${message.replyTo.messageId}`)
                  }
                }}
                style={{
                  fontSize: '10px',
                  textDecoration: 'underline',
                  marginTop: '4px',
                  cursor: 'pointer',
                  backgroundColor: 'transparent',
                  border: 'none',
                  color: colors.primaryAccent,
                  padding: 0,
                }}
              >
                View original
              </button>
            </div>
          )}

          <div
            onTouchStart={() => message.id && handleTouchStart(message.id)}
            onTouchEnd={handleTouchEnd}
            onTouchMove={handleTouchMove}
            style={{
              padding: isMobile ? '8px 10px' : '10px 12px',
              position: 'relative',
              ...messageStyles[message.role],
              transition: 'box-shadow 0.3s ease-in-out',
              ...(isHighlighted && {
                boxShadow: `0 0 0 3px ${colors.primaryAccent}`,
              }),
              ...(isBeingRepliedTo && {
                boxShadow: `0 0 0 2px ${colors.primaryAccent}`,
                opacity: 0.8,
              }),
              userSelect: 'text',
              WebkitUserSelect: 'text',
            }}
          >
            {/* Timestamp & Reply - hidden on mobile until long press */}
            {(!isMobile || longPressedMessageId === message.id) && (
              <div
                style={{
                  position: 'absolute',
                  top: isMobile ? 0 : '6px',
                  right: isMobile ? 0 : '8px',
                  bottom: isMobile ? 0 : 'auto',
                  left: 'auto',
                  color: isMobile ? colors.primaryText : colors.secondaryText,
                  fontSize: isMobile ? '11px' : '10px',
                  textAlign: 'center',
                  opacity: isMobile ? 1 : 0.5,
                  ...(isMobile && {
                    backgroundColor: colors.secondaryBg,
                    border: `1px solid ${colors.primaryBorder}`,
                    padding: '8px 14px',
                    borderRadius: '0 12px 12px 0',
                    display: 'flex',
                    flexDirection: 'column' as const,
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    minWidth: '70px',
                    zIndex: 10,
                  }),
                }}
                onClick={(e) => isMobile && e.stopPropagation()}
                onTouchStart={(e) => e.stopPropagation()}
              >
                {/* Actions row */}
                <div
                  style={{
                    display: 'flex',
                    flexDirection: isMobile ? 'column' : 'row',
                    alignItems: isMobile ? 'stretch' : 'center',
                    gap: '4px',
                    marginBottom: isMobile ? '4px' : 0,
                  }}
                >
                  {message.id && onReplyToMessage && (
                    <button
                      onClick={() => {
                        onReplyToMessage(message)
                        setLongPressedMessageId(null)
                      }}
                      style={{
                        backgroundColor: isMobile ? colors.primaryBg : 'transparent',
                        border: `1px solid ${isMobile ? colors.primaryBorder : 'transparent'}`,
                        borderRadius: '4px',
                        color: colors.primaryText,
                        cursor: 'pointer',
                        fontSize: isMobile ? '11px' : '10px',
                        padding: '2px 6px',
                        opacity: isMobile ? 1 : 0.7,
                        textDecoration: isMobile ? 'none' : 'underline',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        justifyContent: 'center',
                      }}
                      title="Reply to this message"
                    >
                      {isMobile ? '↩ Reply' : 'reply'}
                    </button>
                  )}

                  {isMobile && (
                    <button
                      onClick={() => handleCopyMessage(displayContent, message.id)}
                      style={{
                        backgroundColor: colors.primaryBg,
                        border: `1px solid ${colors.primaryBorder}`,
                        borderRadius: '4px',
                        color: colors.primaryText,
                        cursor: 'pointer',
                        fontSize: '11px',
                        padding: '2px 6px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '4px',
                      }}
                      title="Copy message text"
                    >
                      {copiedMessageId === message.id ? 'Copied' : 'Copy'}
                    </button>
                  )}
                </div>

                <span style={{ color: isMobile ? colors.secondaryText : 'inherit' }}>
                  {message.timestamp.toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </span>
              </div>
            )}

            <div
              style={{
                marginTop: '4px',
                paddingRight: isMobile ? '4px' : '60px',
                fontSize: `${textSize}px`,
                lineHeight: '1.5',
                color: colors.primaryText,
                maxWidth: '800px',
                overflowWrap: 'break-word',
                wordBreak: 'break-word',
              }}
            >
              {message.role === 'assistant' ? (
                <ReactMarkdown
                  remarkPlugins={remarkPlugins}
                  components={{
                    ...markdownComponents,
                    a: ({ href, children }) => {
                      if (href === '#upgrade' && message.onUpgradeClick) {
                        return (
                          <a
                            href="#"
                            onClick={(e) => {
                              e.preventDefault()
                              message.onUpgradeClick?.()
                            }}
                            style={{
                              color: colors.visualAccent1,
                              textDecoration: 'underline',
                              cursor: 'pointer',
                              fontWeight: '600',
                            }}
                          >
                            {children}
                          </a>
                        )
                      }
                      return (
                        <a
                          href={href}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            color: colors.visualAccent1,
                            textDecoration: 'underline',
                          }}
                        >
                          {children}
                        </a>
                      )
                    },
                  }}
                >
                  {normalizeEmojis(displayContent)}
                </ReactMarkdown>
              ) : message.role === 'command' ? (
                <ReactMarkdown
                  remarkPlugins={remarkPlugins}
                  components={{
                    ...markdownComponents,
                    a: ({ href, children }) => {
                      const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
                        if (href?.startsWith('?')) {
                          e.preventDefault()
                          navigate(location.pathname + href)
                        }
                      }
                      return (
                        <a
                          href={href}
                          onClick={handleClick}
                          style={{
                            color: colors.primaryAccent,
                            textDecoration: 'underline',
                            cursor: 'pointer',
                          }}
                        >
                          {children}
                        </a>
                      )
                    },
                  }}
                >
                  {normalizeEmojis(displayContent)}
                </ReactMarkdown>
              ) : message.role === 'user' ? (
                <ReactMarkdown
                  remarkPlugins={remarkPlugins}
                  components={{
                    ...markdownComponents,
                    a: ({ href, children }) => (
                      <a
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          color: colors.visualAccent1,
                          textDecoration: 'underline',
                        }}
                      >
                        {children}
                      </a>
                    ),
                  }}
                >
                  {normalizeEmojis(displayContent)}
                </ReactMarkdown>
              ) : (
                normalizeEmojis(displayContent)
              )}
            </div>

            {/* Tool invocations display */}
            {message.metadata?.toolInvocations && (
              <ToolInvocations toolInvocations={message.metadata.toolInvocations} />
            )}
          </div>
        </div>
      )
    },
    [
      colors,
      copiedMessageId,
      currentSessionId,
      handleCopyMessage,
      handleTouchEnd,
      handleTouchMove,
      handleTouchStart,
      highlightedMessageId,
      isMobile,
      location.pathname,
      longPressedMessageId,
      markdownComponents,
      messageStyles,
      navigate,
      onReplyToMessage,
      replyingToMessageId,
      textSize,
    ]
  )

  // Header component (disclaimer + loading previous)
  const Header = useCallback(() => {
    return (
      <div style={{ paddingTop: '12px' }}>
        {!hasMorePreviousMessages && !isLoadingMessages && (
          <div
            style={{
              textAlign: 'center',
              fontSize: '11px',
              color: colors.secondaryText,
              paddingTop: '0',
              paddingBottom: '12px',
              paddingLeft: '12px',
              paddingRight: '12px',
              opacity: 0.7,
            }}
          >
            {t('chat.disclaimer')}
          </div>
        )}
        {isLoadingPreviousMessages && (
          <Message
            variant="loading"
            style={{
              fontStyle: 'italic',
              paddingTop: theme.spacing.md,
              paddingBottom: theme.spacing.md,
              textAlign: 'center',
            }}
          >
            Loading previous messages...
          </Message>
        )}
      </div>
    )
  }, [colors.secondaryText, hasMorePreviousMessages, isLoadingMessages, isLoadingPreviousMessages, t])

  // Footer component (loading more + bottom padding)
  const Footer = useCallback(() => {
    return (
      <div style={{ paddingBottom: '12px' }}>
        {isLoadingMoreMessages && (
          <Message
            variant="loading"
            style={{
              fontStyle: 'italic',
              paddingTop: theme.spacing.md,
              paddingBottom: theme.spacing.md,
              textAlign: 'center',
            }}
          >
            Loading more messages...
          </Message>
        )}
      </div>
    )
  }, [isLoadingMoreMessages])

  // Empty state
  if (isLoadingMessages) {
    return (
      <div
        style={{
          flex: 1,
          padding: '12px',
          backgroundColor: colors.primaryBg,
          fontFamily,
          fontSize: '13px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Message
          variant="loading"
          style={{
            fontStyle: 'italic',
            paddingTop: theme.spacing.xxl,
            paddingBottom: theme.spacing.xxl,
          }}
        >
          {t('chat.messages.loadingConversation')}
        </Message>
      </div>
    )
  }

  if (messages.length === 0) {
    return (
      <div
        style={{
          flex: 1,
          padding: '12px',
          backgroundColor: colors.primaryBg,
          fontFamily,
          fontSize: '13px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Message
          variant="info"
          style={{
            fontStyle: 'italic',
            paddingTop: theme.spacing.xxl,
            paddingBottom: theme.spacing.xxl,
          }}
        >
          {t('chat.messages.noMessages')}
        </Message>
      </div>
    )
  }

  return (
    <div style={{ flex: 1, position: 'relative', display: 'flex', flexDirection: 'column' }}>
      <Virtuoso
        ref={virtuosoRef}
        style={{
          flex: 1,
          backgroundColor: colors.primaryBg,
          fontFamily,
          fontSize: '13px',
        }}
        data={messages}
        firstItemIndex={firstItemIndex}
        initialTopMostItemIndex={messages.length - 1}
        itemContent={renderMessage}
        components={{
          Header,
          Footer,
        }}
        startReached={handleStartReached}
        increaseViewportBy={{ top: 200, bottom: 200 }}
        atBottomStateChange={setIsAtBottom}
      />
      {!isAtBottom && (
        <button
          onClick={handleScrollToBottom}
          style={{
            position: 'absolute',
            bottom: '16px',
            right: '16px',
            width: '32px',
            height: '32px',
            borderRadius: '50%',
            backgroundColor: colors.secondaryBg,
            border: `1px solid ${colors.primaryBorder}`,
            color: colors.primaryText,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '14px',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
            opacity: 0.9,
            transition: 'opacity 0.2s, transform 0.2s',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.opacity = '1'
            e.currentTarget.style.transform = 'scale(1.05)'
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.opacity = '0.9'
            e.currentTarget.style.transform = 'scale(1)'
          }}
          title="Scroll to bottom"
        >
          ↓
        </button>
      )}
    </div>
  )
}

export default MessagesDisplay
